/**
 * StageFieldEditor
 *
 * Edits the list of fields for a single workflow stage.
 * Each field row: drag order (up/down), label, field_type dropdown,
 * required toggle, options editor (for select/multiselect), help text.
 */

import React, { useState } from 'react';
import {
  Box, TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, IconButton, Chip, Typography,
  Paper, Button, Tooltip, Divider,
} from '@mui/material';
import { ArrowUp, ArrowDown, Trash2, Plus, X } from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'select', label: 'Select (single)' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'photo', label: 'Photo' },
];

const NEEDS_OPTIONS = ['select', 'multiselect'];

const emptyField = () => ({
  _id: Math.random().toString(36).slice(2),
  field_key: '',
  label: '',
  field_type: 'text',
  options: [],
  is_required: false,
  help_text: '',
});

const StageFieldEditor = ({ fields = [], onChange }) => {
  const [optionInputs, setOptionInputs] = useState({}); // { _id: inputText }

  const update = (updated) => onChange(updated);

  const addField = () => update([...fields, emptyField()]);

  const remove = (id) => update(fields.filter(f => f._id !== id));

  const move = (id, dir) => {
    const idx = fields.findIndex(f => f._id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    const arr = [...fields];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    update(arr);
  };

  const setField = (id, key, value) => {
    update(fields.map(f => {
      if (f._id !== id) return f;
      const updated = { ...f, [key]: value };
      // Auto-generate field_key from label if not manually set
      if (key === 'label' && (!f.field_key || f.field_key === slugify(f.label))) {
        updated.field_key = slugify(value);
      }
      return updated;
    }));
  };

  const addOption = (id) => {
    const text = (optionInputs[id] || '').trim();
    if (!text) return;
    const field = fields.find(f => f._id === id);
    if (!field) return;
    const opts = [...(field.options || [])];
    if (!opts.includes(text)) opts.push(text);
    update(fields.map(f => f._id === id ? { ...f, options: opts } : f));
    setOptionInputs(prev => ({ ...prev, [id]: '' }));
  };

  const removeOption = (id, opt) => {
    update(fields.map(f =>
      f._id === id ? { ...f, options: (f.options || []).filter(o => o !== opt) } : f
    ));
  };

  return (
    <Box>
      {fields.map((field, idx) => (
        <Paper key={field._id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {/* Order controls */}
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" disabled={idx === 0} onClick={() => move(field._id, -1)}>
                    <ArrowUp size={14} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton size="small" disabled={idx === fields.length - 1} onClick={() => move(field._id, 1)}>
                    <ArrowDown size={14} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* Main row */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  label="Label"
                  value={field.label}
                  onChange={e => setField(field._id, 'label', e.target.value)}
                  size="small"
                  sx={{ flex: 2, minWidth: 160 }}
                  required
                />
                <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
                  <InputLabel>Field Type</InputLabel>
                  <Select
                    value={field.field_type}
                    label="Field Type"
                    onChange={e => setField(field._id, 'field_type', e.target.value)}
                  >
                    {FIELD_TYPES.map(t => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.is_required}
                      onChange={e => setField(field._id, 'is_required', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Required"
                  sx={{
                    whiteSpace: 'nowrap',
                    mt: 0.5,
                    ml: 1,
                    '& .MuiFormControlLabel-label': { ml: 1 },
                  }}
                />
              </Box>

              <TextField
                label="Help text (optional)"
                value={field.help_text || ''}
                onChange={e => setField(field._id, 'help_text', e.target.value)}
                size="small"
                fullWidth
              />

              {/* Options editor for select/multiselect */}
              {NEEDS_OPTIONS.includes(field.field_type) && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Options</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 1 }}>
                    {(field.options || []).map(opt => (
                      <Chip
                        key={opt}
                        label={opt}
                        size="small"
                        onDelete={() => removeOption(field._id, opt)}
                        deleteIcon={<X size={12} />}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      placeholder="Add option…"
                      value={optionInputs[field._id] || ''}
                      onChange={e => setOptionInputs(prev => ({ ...prev, [field._id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addOption(field._id))}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <Button size="small" variant="outlined" onClick={() => addOption(field._id)} startIcon={<Plus size={14} />}>
                      Add
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Remove */}
            <Tooltip title="Remove field">
              <IconButton size="small" color="error" onClick={() => remove(field._id)}>
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      ))}

      <Button
        size="small"
        startIcon={<Plus size={14} />}
        onClick={addField}
        variant="outlined"
        sx={{ mt: 0.5 }}
      >
        Add field
      </Button>
    </Box>
  );
};

// Simple label → snake_case
const slugify = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

export default StageFieldEditor;
